import { useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Check, ChevronDown, GripVertical, Plus, Search, Trash2, Upload, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { parseGuestsCsv } from '@/lib/seatingPlannerCsv'
import type { SeatAssignment, SeatingGuest, SeatingTable } from '@/types/seatingPlanner'

type GuestPanelProps = {
  guests: SeatingGuest[]
  tables: SeatingTable[]
  assignments: Record<string, SeatAssignment>
  onAddGuest: (guest: SeatingGuest) => void
  onAddGuests: (guests: SeatingGuest[]) => void
  onUpdateGuest: (id: string, patch: Partial<SeatingGuest>) => void
  onDeleteGuest: (id: string) => void
  onUnassignGuest: (id: string) => void
  onAssignGuestToTable: (guestId: string, tableId: string) => void
}

export default function GuestPanel({
  guests,
  tables,
  assignments,
  onAddGuest,
  onAddGuests,
  onUpdateGuest,
  onDeleteGuest,
  onUnassignGuest,
  onAssignGuestToTable,
}: GuestPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [query, setQuery] = useState('')

  const unassignedAll = guests.filter((g) => !assignments[g.id])
  const assignedAll = guests.filter((g) => assignments[g.id])
  const unassigned = query ? unassignedAll.filter((g) => matchesQuery(g, query)) : unassignedAll
  const assigned = query ? assignedAll.filter((g) => matchesQuery(g, query)) : assignedAll
  const hasActiveQuery = query.trim().length > 0
  const hasNoMatches = hasActiveQuery && unassigned.length === 0 && assigned.length === 0

  const tablesById = new Map(tables.map((t) => [t.id, t]))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    onAddGuest({
      id: cryptoRandomId(),
      name: trimmed,
    })
    setName('')
  }

  const handleCsvFile = async (file: File) => {
    try {
      const result = await parseGuestsCsv(file, guests)
      onAddGuests(result.guests)
      toast.success(
        `Imported ${result.guests.length} guest${result.guests.length === 1 ? '' : 's'}` +
          (result.skipped > 0 ? ` — skipped ${result.skipped} empty/duplicate row${result.skipped === 1 ? '' : 's'}` : ''),
      )
    } catch (err) {
      console.error(err)
      toast.error('Could not parse that CSV. Make sure it has a "name" column.')
    }
  }

  return (
    <aside className="flex h-full min-h-0 w-80 shrink-0 flex-col gap-4 border-r border-black/5 bg-muted/30 p-4">
      <div>
        <h2 className="text-base font-semibold tracking-tight">Guests</h2>
        <p className="text-xs text-muted-foreground">
          Drag a guest onto a table to assign a seat.
        </p>
      </div>

      <form className="space-y-2" onSubmit={handleSubmit}>
        <Label htmlFor="guest-name" className="text-xs font-medium">
          Add guest manually
        </Label>
        <Input
          id="guest-name"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex gap-2">
          <Button type="submit" size="sm" className="flex-1">
            <UserPlus className="mr-1.5 size-4" />
            Add
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-1.5 size-4" />
            CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleCsvFile(file)
              e.target.value = ''
            }}
          />
        </div>
      </form>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guests…"
          className="pr-8 pl-8"
          aria-label="Search guests"
        />
        {hasActiveQuery && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {hasNoMatches ? (
          <EmptyHint>No guests match &ldquo;{query}&rdquo;.</EmptyHint>
        ) : (
          <>
            <Section
              title={
                hasActiveQuery
                  ? `Unassigned (${unassigned.length} / ${unassignedAll.length})`
                  : `Unassigned (${unassignedAll.length})`
              }
            >
              {unassigned.length === 0 ? (
                <EmptyHint>{hasActiveQuery ? 'No matches.' : 'No unassigned guests.'}</EmptyHint>
              ) : (
                unassigned.map((g) => (
                  <DraggableGuest
                    key={g.id}
                    guest={g}
                    tables={tables}
                    currentTableId={undefined}
                    onDelete={() => onDeleteGuest(g.id)}
                    onRename={(value) => onUpdateGuest(g.id, { name: value })}
                    onAssignToTable={(tableId) => onAssignGuestToTable(g.id, tableId)}
                  />
                ))
              )}
            </Section>
            <Section
              title={
                hasActiveQuery
                  ? `Seated (${assigned.length} / ${assignedAll.length})`
                  : `Seated (${assignedAll.length})`
              }
            >
              {assigned.length === 0 ? (
                <EmptyHint>{hasActiveQuery ? 'No matches.' : 'No one seated yet.'}</EmptyHint>
              ) : (
                assigned.map((g) => {
                  const assignment = assignments[g.id]
                  const table = assignment ? tablesById.get(assignment.tableId) : undefined
                  return (
                    <DraggableGuest
                      key={g.id}
                      guest={g}
                      tables={tables}
                      currentTableId={assignment?.tableId}
                      tableNumber={table?.number}
                      onDelete={() => onDeleteGuest(g.id)}
                      onUnassign={() => onUnassignGuest(g.id)}
                      onRename={(value) => onUpdateGuest(g.id, { name: value })}
                      onAssignToTable={(newTableId) => onAssignGuestToTable(g.id, newTableId)}
                    />
                  )
                })
              )}
            </Section>
          </>
        )}
      </div>
    </aside>
  )
}

function matchesQuery(guest: SeatingGuest, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const haystack = [guest.name, guest.notes].filter(Boolean).join(' ').toLowerCase()
  return haystack.includes(q)
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{title}</h3>
      <div className="space-y-1">{children}</div>
    </section>
  )
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded border border-dashed border-black/10 px-2 py-3 text-center text-xs text-muted-foreground">
      {children}
    </div>
  )
}

function DraggableGuest({
  guest,
  tables,
  currentTableId,
  tableNumber,
  onDelete,
  onUnassign,
  onRename,
  onAssignToTable,
}: {
  guest: SeatingGuest
  tables: SeatingTable[]
  currentTableId?: string
  tableNumber?: string
  onDelete: () => void
  onUnassign?: () => void
  onRename: (value: string) => void
  onAssignToTable: (tableId: string) => void
}) {
  const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
    id: `guest:${guest.id}`,
    data: { type: 'guest', guestId: guest.id },
  })

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(guest.name)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'group flex items-center gap-1 rounded-md border border-black/5 bg-white px-1.5 py-1 text-sm shadow-sm transition-colors hover:border-primary/30',
        isDragging && 'opacity-40',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground active:cursor-grabbing"
        aria-label="Drag guest"
      >
        <GripVertical className="size-3.5" />
      </button>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setEditing(false)
            const trimmed = draft.trim()
            if (trimmed && trimmed !== guest.name) onRename(trimmed)
            else setDraft(guest.name)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            if (e.key === 'Escape') {
              setDraft(guest.name)
              setEditing(false)
            }
          }}
          className="flex-1 rounded border border-primary/40 bg-white px-1 text-sm outline-hidden"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex-1 truncate text-left font-medium"
        >
          {guest.name}
        </button>
      )}
      <SeatMenu
        tables={tables}
        currentTableId={currentTableId}
        tableNumber={tableNumber}
        onAssignToTable={onAssignToTable}
        onUnassign={onUnassign}
      />
      <button
        type="button"
        onClick={onDelete}
        className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-600"
        aria-label="Delete guest"
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  )
}

function SeatMenu({
  tables,
  currentTableId,
  tableNumber,
  onAssignToTable,
  onUnassign,
}: {
  tables: SeatingTable[]
  currentTableId?: string
  tableNumber?: string
  onAssignToTable: (tableId: string) => void
  onUnassign?: () => void
}) {
  if (tables.length === 0) {
    return (
      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        No tables
      </span>
    )
  }

  const sorted = [...tables].sort((a, b) =>
    (a.number || '').localeCompare(b.number || '', undefined, { numeric: true, sensitivity: 'base' }),
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={tableNumber ? `Change table (currently Table ${tableNumber})` : 'Seat at a table'}
          className={cn(
            'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
            tableNumber
              ? 'bg-primary/10 text-primary hover:bg-primary/20'
              : 'border border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary',
          )}
        >
          {tableNumber ? `T${tableNumber}` : 'Seat'}
          <ChevronDown className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        {sorted.map((t) => {
          const selected = t.id === currentTableId
          const headline = t.label?.trim() ? `Table ${t.number || '?'} — ${t.label}` : `Table ${t.number || '?'}`
          return (
            <DropdownMenuItem
              key={t.id}
              onSelect={() => onAssignToTable(t.id)}
              className={cn('gap-2', selected && 'bg-accent text-accent-foreground')}
            >
              <Check className={cn('size-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
              <span className="truncate">{headline}</span>
            </DropdownMenuItem>
          )
        })}
        {onUnassign && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onUnassign} className="text-red-600 focus:text-red-700">
              <X className="mr-1 size-3.5" />
              Unassign
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function NewGuestButton({ onAdd }: { onAdd: () => void }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onAdd}>
      <Plus className="mr-1 size-4" />
      Add table
    </Button>
  )
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `g_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
}
